import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useGetFancyBetsQuery, useGetFancyDropdownQuery } from '../services/sports/sportsApi';
import { useLazySearchUsersQuery } from '../services/auth/authApi';
import Loading from '../components/Loading';

const BackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const EmptyState = () => (
  <tr>
    <td colSpan="10" className="py-16 text-center">
      <div className="flex flex-col items-center gap-3 text-slate-400">
        <svg className="w-12 h-12 text-slate-300" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
          <g transform="translate(0 1)" fill="none" fillRule="evenodd">
            <ellipse fill="#e2e8f0" cx="32" cy="33" rx="32" ry="7"/>
            <g fill="#cbd5e1" fillRule="nonzero">
              <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"/>
              <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z"/>
            </g>
          </g>
        </svg>
        <p className="text-sm font-medium">No bets found</p>
        <p className="text-xs text-slate-400">Try adjusting your search or filters</p>
      </div>
    </td>
  </tr>
);

const FancyBetsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { matchId, matchName } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedFancy, setSelectedFancy] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const [searchUsers, { isLoading: searchLoading }] = useLazySearchUsersQuery();

  const { data: fancyDropdownData } = useGetFancyDropdownQuery(
    { eventId: matchId },
    { skip: !matchId }
  );

  console.log("FancyBetsPage loaded - matchId:", matchId, "matchName:", matchName, "location:", location.pathname);

  const { data: apiData, isLoading, error: apiError } = useGetFancyBetsQuery({
    eventId: matchId,
    fancyId: selectedFancy || undefined,
    search: searchQuery || undefined,
    page: currentPage,
    limit,
  }, { skip: !matchId });

  const sessionBets = apiData?.data || [];
  const pagination = apiData?.pagination || {};
  const error = apiError ? (apiError.message || 'Failed to load fancy bets') : null;

  const handleSearchInput = async (value) => {
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!value.trim()) { setSearchResults([]); setShowDropdown(false); return; }
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await searchUsers(value.trim()).unwrap();
        if (result?.success && result?.data) {
          setSearchResults(result.data);
          setShowDropdown(true);
        } else {
          setSearchResults([]); setShowDropdown(false);
        }
      } catch {
        setSearchResults([]); setShowDropdown(false);
      }
    }, 300);
  };

  const handleSelectUser = (user) => {
    setSearchInput(user.userId || user.userName);
    setSearchQuery(user.userId || user.userName);
    setShowDropdown(false);
    setCurrentPage(1);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <div className="w-full animate-fadeIn">
      <div className="bets-table bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">

        {/* ── Page Header ────────────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-[#0c1524] to-[#162032] px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 bg-teal-400 rounded-full" />
            <h1 className="text-white font-semibold text-base tracking-wide">Fancy Bets</h1>
            {matchName && (
              <span className="text-teal-400/80 text-xs font-medium hidden sm:block">
                — {decodeURIComponent(matchName)}
              </span>
            )}
          </div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-sm font-medium px-3 py-1.5 rounded-lg transition-all"
          >
            <BackIcon />
            Back
          </button>
        </div>

        {/* ── Filters ────────────────────────────────────────────────── */}
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center">

            {/* Fancy dropdown */}
            <select
              value={selectedFancy}
              onChange={(e) => { setSelectedFancy(e.target.value); setCurrentPage(1); }}
              className="sm:w-52 px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all"
            >
              <option value="">All Fancy</option>
              {fancyDropdownData?.data?.map((fancy) => (
                <option key={fancy.fancyId} value={fancy.fancyId}>{fancy.fancyName}</option>
              ))}
            </select>

            {/* User search */}
            <div className="relative flex-1" ref={dropdownRef}>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                  <SearchIcon />
                </span>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => handleSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchInput.trim()) {
                      setSearchQuery(searchInput);
                      setShowDropdown(false);
                      setCurrentPage(1);
                    }
                  }}
                  onFocus={() => { if (searchInput.trim() && searchResults.length > 0) setShowDropdown(true); }}
                  placeholder="Search by User ID or Name…"
                  className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all"
                />
              </div>
              {showDropdown && searchResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl max-h-56 overflow-auto">
                  {searchLoading ? (
                    <div className="px-4 py-3 text-sm text-slate-500">Searching…</div>
                  ) : (
                    searchResults.map((u, i) => (
                      <div
                        key={u.id || i}
                        onClick={() => handleSelectUser(u)}
                        className="px-4 py-2.5 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0 transition-colors"
                      >
                        <p className="text-sm font-semibold text-slate-800">{u.userName || u.userId}</p>
                        <p className="text-xs text-slate-400">{u.userId}{u.fullName ? ` — ${u.fullName}` : ''}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Action buttons */}
            <button
              onClick={() => { setSearchQuery(searchInput); setShowDropdown(false); setCurrentPage(1); }}
              className="flex items-center gap-1.5 bg-[#0c1524] hover:bg-[#162032] text-white text-sm font-medium px-4 py-2 rounded-lg transition-all"
            >
              <SearchIcon />
              Search
            </button>
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(''); setSearchInput(''); setSearchResults([]); setShowDropdown(false); setCurrentPage(1); }}
                className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-medium px-3 py-2 rounded-lg transition-all"
              >
                <XIcon />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* ── Table ──────────────────────────────────────────────────── */}
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex justify-center items-center py-16">
              <Loading />
            </div>
          ) : error ? (
            <div className="py-10 text-center">
              <p className="text-red-500 text-sm">{error}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#0F2327] text-white">
                  {['Run','Rate','Amount','Type','Team','Client','Agent','Date','Loss','Profit'].map(h => (
                    <th key={h} className="px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap border-b border-gray-600">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sessionBets.length === 0 ? (
                  <EmptyState />
                ) : (
                  sessionBets.map((bet, index) => {
                    const isYes = bet.type?.toLowerCase() === 'back';
                    return (
                      <tr
                        key={bet.id || index}
                        className={`border-b border-slate-100 ${isYes ? 'bet-row-back' : 'bet-row-lay'}`}
                      >
                        <td className="px-3 py-2.5 text-slate-700">{bet.rate || '-'}</td>
                        <td className="px-3 py-2.5 text-slate-700">{bet.volume || '-'}</td>
                        <td className="px-3 py-2.5 font-medium text-slate-800">{bet.amount || '-'}</td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold
                            ${isYes ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                            {bet.type?.toLowerCase() === 'back' ? 'Yes' : bet.type?.toLowerCase() === 'lay' ? 'No' : bet.type || '-'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-slate-700">{bet.team || '-'}</td>
                        <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{bet.userId} ({bet.fullName})</td>
                        <td className="px-3 py-2.5 text-slate-600">{bet.agent || '-'}</td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs whitespace-nowrap">{formatDate(bet.date)}</td>
                        <td className="px-3 py-2.5 font-medium text-red-600">{bet.loss || 0}</td>
                        <td className="px-3 py-2.5 font-medium text-emerald-600">{bet.profit || 0}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Pagination ─────────────────────────────────────────────── */}
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-slate-500">
            Page <span className="font-semibold text-slate-700">{pagination.page}</span> of{' '}
            <span className="font-semibold text-slate-700">{pagination.totalPages}</span>
            {' '}·{' '}
            <span className="font-semibold text-slate-700">{pagination.totalDocs}</span> total bets
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => p - 1)}
              disabled={currentPage === 1}
              className="px-4 py-1.5 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              ← Previous
            </button>
            <span className="px-3 py-1.5 text-sm font-semibold bg-[#0c1524] text-white rounded-lg">
              {currentPage}
            </span>
            <button
              onClick={() => setCurrentPage(p => p + 1)}
              disabled={currentPage >= pagination.totalPages}
              className="px-4 py-1.5 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              Next →
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default FancyBetsPage;
